import { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const ease = [0.22, 1, 0.36, 1];

export default function FinalCTA() {
  const ref = useRef(null);
  const [videoReady, setVideoReady] = useState(false);
  const [inView, setInView] = useState(false);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });

  const scale = useTransform(scrollYProgress, [0, 0.4], [0.92, 1]);
  const opacity = useTransform(scrollYProgress, [0, 0.35], [0, 1]);

  // Detect when section enters viewport
  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); observer.disconnect(); } },
      { rootMargin: "-10% 0px" }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  // Fallback: show text after 1s even if video hasn't fired onCanPlay
  useEffect(() => {
    if (inView && !videoReady) {
      const t = setTimeout(() => setVideoReady(true), 800);
      return () => clearTimeout(t);
    }
  }, [inView, videoReady]);

  const showText = inView;

  return (
    <section
      id="contact"
      ref={ref}
      data-testid="final-cta-section"
      className="relative py-40 md:py-56 overflow-hidden"
    >
      {/* Background Video */}
      <video
        autoPlay
        loop
        muted
        playsInline
        onCanPlay={() => setVideoReady(true)}
        className="absolute inset-0 w-full h-full object-cover pointer-events-none transition-opacity duration-1000"
        style={{ opacity: inView ? 0.25 : 0, mixBlendMode: "lighten" }}
      >
        <source src="https://customer-assets.emergentagent.com/job_deva-command-nexus/artifacts/rzdbe1hu_VID-20260418-WA0003.mp4" type="video/mp4" />
      </video>

      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
        <div className="w-[500px] h-[500px] md:w-[800px] md:h-[800px] rounded-full bg-[#00E5FF]/[0.05] blur-[120px] animate-pulse-slow" />
      </div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
        <div className="w-[350px] h-[350px] md:w-[550px] md:h-[550px] rounded-full bg-[#7A5CFF]/[0.04] blur-[100px] animate-pulse-slow" style={{ animationDelay: "-3s" }} />
      </div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
        <div className="w-[200px] h-[200px] md:w-[350px] md:h-[350px] rounded-full bg-[#FF2D9A]/[0.03] blur-[80px] animate-pulse-slow" style={{ animationDelay: "-1.5s" }} />
      </div>

      <motion.div
        style={{ scale, opacity }}
        className="relative z-10 w-full text-center px-6"
      >
        <motion.p
          animate={{ opacity: showText ? 1 : 0, y: showText ? 0 : 20 }}
          transition={{ duration: 1, delay: 0.2, ease }}
          className="sys-label mb-10 md:mb-14"
        >
          SYS.STANDBY //
        </motion.p>

        <motion.h2
          animate={{ opacity: showText ? 1 : 0, y: showText ? 0 : 30 }}
          transition={{ duration: 1.2, delay: 0.5, ease }}
          className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extralight tracking-tighter text-white mb-5 pop-click"
        >
          Enter The{" "}
          <span className="showcase-accent bg-gradient-to-r from-[#00E5FF] via-[#7A5CFF] to-[#FF2D9A] bg-clip-text text-transparent inline-block">Tech Temple</span>.
        </motion.h2>

        <motion.p
          animate={{ opacity: showText ? 1 : 0, y: showText ? 0 : 20 }}
          transition={{ duration: 1, delay: 0.8, ease }}
          className="font-body text-base md:text-lg text-zinc-500 font-light mb-16 pop-click"
        >
          Build with Devamind.
        </motion.p>

        <motion.div
          animate={{ opacity: showText ? 1 : 0, y: showText ? 0 : 20 }}
          transition={{ duration: 1, delay: 1.1, ease }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6"
        >
          <a
            href="mailto:hello@devamind.in"
            data-testid="cta-start-project-btn"
            className="group relative px-10 py-4 border border-[#00E5FF]/25 text-[#00E5FF] font-mono text-[10px] tracking-[0.25em] uppercase overflow-hidden transition-all duration-700 hover:border-[#00E5FF]/60 hover:shadow-[0_0_30px_rgba(0,229,255,0.1)]"
          >
            <span className="relative z-10">Start Project</span>
            <div className="absolute inset-0 bg-[#00E5FF]/[0.04] translate-y-full group-hover:translate-y-0 transition-transform duration-700 ease-out" />
          </a>
          <a
            href="mailto:hello@devamind.in"
            data-testid="cta-contact-btn"
            className="px-10 py-4 bg-white/[0.02] border border-white/[0.08] text-white/70 font-mono text-[10px] tracking-[0.25em] uppercase transition-all duration-700 hover:bg-white/[0.04] hover:border-white/15 hover:text-white"
          >
            Contact
          </a>
        </motion.div>
      </motion.div>
    </section>
  );
}
